import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { get, post } from '../lib/api';
import { ErrorNote, Spinner } from '../components/ui';
import type { Pet, PetAccessory, PetSpecies } from '../lib/types';

// three.js is a big download; it only loads once a pet actually needs drawing.
const PetCanvas = lazy(() => import('../pet/PetCanvas'));

const SLOTS: { id: PetAccessory['slot']; label: string }[] = [
  { id: 'head', label: 'Head' },
  { id: 'face', label: 'Face' },
  { id: 'neck', label: 'Neck' },
  { id: 'aura', label: 'Extra' },
];

const STAGE_NAMES = ['Baby', 'Young', 'Growing', 'Grown', 'Legendary'];

/** Thrown up on every tap, so no two taps look quite the same. */
const TAP_GLYPHS = ['💛', '💖', '✨', '💫', '⭐'];
const STAR_GLYPHS = ['⭐', '✨', '🌟', '💫'];

/** Taps counted so far today, kept per child so closing the app doesn't lose them. */
function playKey(name: string): string {
  return `play:${name}:${new Date().toISOString().slice(0, 10)}`;
}

function readTaps(name: string): number {
  try {
    return Number(localStorage.getItem(playKey(name)) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function writeTaps(name: string, taps: number): void {
  try {
    localStorage.setItem(playKey(name), String(taps));
  } catch {
    /* private browsing: the counter just restarts, which is no great loss */
  }
}

export default function PetPage(): JSX.Element {
  const [pet, setPet] = useState<Pet | null>(null);
  const [species, setSpecies] = useState<PetSpecies[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taps, setTaps] = useState(0);
  const [hearts, setHearts] = useState<{ id: number; left: number; glyph: string }[]>([]);
  const [stars, setStars] = useState<{ id: number; left: number; delay: number; glyph: string }[]>([]);
  const [cheer, setCheer] = useState(0);
  const [justPlayed, setJustPlayed] = useState(false);
  const heartId = useRef(0);
  const claiming = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await get<{ pet: Pet | null; species: PetSpecies[] }>('/api/pet');
      setPet(res.pet);
      setSpecies(res.species);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (pet) setTaps(readTaps(pet.name));
  }, [pet?.name]);

  /**
   * Playing is free: tap the pet ten times and it is worth one experience
   * point, once a day. The server decides whether it counts, so tapping on two
   * phones or reinstalling the app cannot earn it twice.
   */
  const petTap = useCallback(() => {
    if (!pet) return;
    setHearts((current) => [
      ...current.slice(-6),
      {
        id: (heartId.current += 1),
        left: 28 + Math.random() * 44,
        glyph: TAP_GLYPHS[Math.floor(Math.random() * TAP_GLYPHS.length)],
      },
    ]);
    if (pet.playedToday) return;
    setTaps((current) => {
      const next = current + 1;
      writeTaps(pet.name, next);
      if (next >= pet.playTaps && !claiming.current) {
        claiming.current = true;
        post<{ pet: Pet; awarded: boolean }>('/api/pet/play')
          .then((res) => {
            setPet(res.pet);
            if (res.awarded) {
              setJustPlayed(true);
              rain();
            }
          })
          .catch((err) => setError((err as Error).message))
          .finally(() => {
            claiming.current = false;
          });
      }
      return next;
    });
  }, [pet]);

  useEffect(() => {
    if (hearts.length === 0) return;
    const timer = setTimeout(() => setHearts((current) => current.slice(1)), 900);
    return () => clearTimeout(timer);
  }, [hearts]);

  /** The day's play is done: the pet spins, and stars fall on it. */
  function rain() {
    setCheer((current) => current + 1);
    setStars(
      Array.from({ length: 18 }, (_, i) => ({
        id: Date.now() + i,
        left: Math.random() * 92,
        delay: Math.random() * 0.5,
        glyph: STAR_GLYPHS[i % STAR_GLYPHS.length],
      })),
    );
    setTimeout(() => setStars([]), 2200);
  }

  async function equip(slot: PetAccessory['slot'], accessory: string | null) {
    try {
      const res = await post<{ pet: Pet }>('/api/pet/equip', { slot, accessory });
      setPet(res.pet);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!loaded) return <Spinner />;
  if (!pet) return <AdoptPet species={species} onAdopted={setPet} />;

  const progress = pet.xpForNextLevel === null ? 1 : pet.xpIntoLevel / pet.xpForNextLevel;

  return (
    <>
      <ErrorNote error={error} />
      <div className="q-hello">{pet.name}</div>
      <span className="q-streak">
        Level {pet.level} · {STAGE_NAMES[pet.stage - 1]}
      </span>

      <div className="pet-stage">
        <Suspense fallback={<div className="pet-canvas" style={{ height: 300 }} />}>
          <PetCanvas
            species={pet.species}
            stage={pet.stage}
            equipped={pet.equipped}
            height={300}
            onPoke={petTap}
            cheer={cheer}
          />
        </Suspense>
        {hearts.map((heart) => (
          <span key={heart.id} className="pet-heart" style={{ left: `${heart.left}%` }}>
            {heart.glyph}
          </span>
        ))}
        {stars.map((star) => (
          <span
            key={star.id}
            className="pet-star"
            style={{ left: `${star.left}%`, animationDelay: `${star.delay}s` }}
          >
            {star.glyph}
          </span>
        ))}
        {stars.length > 0 && <span className="pet-xp-pop">+1 XP</span>}
      </div>

      <div className={`pet-play${pet.playedToday ? ' done' : ''}`}>
        {pet.playedToday ? (
          <span>
            {justPlayed ? `${pet.name} loved that! +1 XP` : `You played with ${pet.name} today ✓`}
            <span className="q-goal-hint" style={{ display: 'block' }}>
              Free play comes back tomorrow — but keep tapping, they don't mind.
            </span>
          </span>
        ) : (
          <span>
            Tap {pet.name} {pet.playTaps} times to play · +1 XP
            <span className="pet-play-track">
              <span style={{ width: `${Math.min(100, (taps / pet.playTaps) * 100)}%` }} />
            </span>
          </span>
        )}
      </div>

      <div className="q-goal" style={{ marginTop: 0 }}>
        <div className="q-goal-row">
          <span>{pet.xpForNextLevel === null ? 'Fully grown' : `Level ${pet.level + 1}`}</span>
          <span>
            {pet.xpForNextLevel === null ? `${pet.xp} XP` : `${pet.xpIntoLevel} / ${pet.xpForNextLevel} XP`}
          </span>
        </div>
        <div className="q-track">
          <span style={{ width: `${Math.min(100, progress * 100)}%` }} />
        </div>
        <div className="q-goal-hint">
          Chores feed {pet.name}. Doing one before anyone reminds you is worth four times as much.
        </div>
      </div>

      <h2>Wardrobe</h2>
      {SLOTS.map((slot) => {
        const options = pet.accessories.filter((a) => a.slot === slot.id);
        if (options.length === 0) return null;
        return (
          <div key={slot.id} style={{ marginBottom: 14 }}>
            <div className="q-goal-hint" style={{ marginBottom: 6 }}>{slot.label}</div>
            <div className="chips">
              <button
                type="button"
                className={`chip${pet.equipped[slot.id] ? '' : ' on'}`}
                onClick={() => void equip(slot.id, null)}
              >
                None
              </button>
              {options.map((accessory) => {
                const unlocked = pet.unlocked.includes(accessory.id);
                return (
                  <button
                    key={accessory.id}
                    type="button"
                    className={`chip${pet.equipped[slot.id] === accessory.id ? ' on' : ''}${unlocked ? '' : ' locked'}`}
                    disabled={!unlocked}
                    onClick={() => void equip(slot.id, accessory.id)}
                  >
                    {unlocked ? accessory.name : `🔒 ${accessory.name} · lvl ${accessory.level}`}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {pet.recent.length > 0 && (
        <>
          <h2>Recently fed by</h2>
          <div className="q-goal" style={{ marginTop: 0, padding: '4px 16px' }}>
            {pet.recent.map((entry, index) => (
              <div key={index} className="q-claim">
                <span className="grow">{entry.reason}</span>
                <span className="q-coins">+{entry.amount} XP</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function AdoptPet({ species, onAdopted }: { species: PetSpecies[]; onAdopted: (pet: Pet) => void }): JSX.Element {
  const [picked, setPicked] = useState<string>(species[0]?.id ?? 'cat');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function adopt() {
    if (!name.trim()) {
      setError('Give them a name first.');
      return;
    }
    setBusy(true);
    try {
      const res = await post<{ pet: Pet }>('/api/pet', { species: picked, name: name.trim() });
      onAdopted(res.pet);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="q-hello">Choose your pet</div>
      <p className="q-goal-hint">They grow every time you do your chores. You only pick once, so choose well.</p>
      <ErrorNote error={error} />
      {species.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`q-row${picked === option.id ? ' picked' : ''}`}
          onClick={() => setPicked(option.id)}
          style={{ marginTop: 10 }}
        >
          <span className="grow" style={{ textAlign: 'left' }}>
            <span className="q-title">{option.name}</span>
            <span className="q-meta">{option.blurb}</span>
          </span>
        </button>
      ))}
      <div className="pet-stage" style={{ marginTop: 14 }}>
        <Suspense fallback={<div className="pet-canvas" style={{ height: 240 }} />}>
          <PetCanvas species={picked} stage={1} equipped={{}} height={240} />
        </Suspense>
      </div>
      <label className="field" style={{ marginTop: 12 }}>
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={20} placeholder="Miso" />
      </label>
      <button className="btn block" type="button" onClick={adopt} disabled={busy}>
        {busy ? 'Saying hello…' : `Adopt ${name.trim() || 'them'}`}
      </button>
    </>
  );
}
